import { ColumnDef } from '@tanstack/react-table';
import { ActionDropdown } from './ActionDropdown';
import { User } from '../types';
import {
  UserInviteStatus,
  UserStatus,
  INVITE_STATUS_LABELS,
  USER_STATUS_LABELS,
  ROLES,
} from '../constants';
import { TruncatedCell } from '@/components';

const getStatusChipClass = (status: UserStatus | UserInviteStatus) => {
  switch (status) {
    case UserStatus.ACTIVE:
      return 'text-foreground';
    case UserInviteStatus.PENDING:
      return 'bg-black text-white';
    case UserStatus.UNRESPONSIVE:
    case UserInviteStatus.EXPIRED:
    case UserStatus.INACTIVE:
      return 'bg-gray-100 text-gray-900';
    default:
      return 'text-foreground';
  }
};

export const columns: ColumnDef<User>[] = [
  {
    header: 'User',
    accessorKey: 'firstName',
    cell: ({ row }) => {
      const name = `${row.original.firstName} ${row.original.lastName}`;
      return (
        <div className="flex items-center gap-2">
          <TruncatedCell text={name} />
          {row.original.isCurrentUser && (
            <span className="text-secondary-foreground shrink-0 rounded-full bg-neutral-200 px-2.5 py-0.5 text-xs">
              This Is You!
            </span>
          )}
        </div>
      );
    },
  },
  {
    header: 'Email',
    accessorKey: 'emailAddress',
  },
  {
    header: 'Role',
    accessorKey: 'role',
    cell: ({ row }) => {
      const role = row.original.role;
      const label = ROLES.find((r) => r.value === role)?.label;
      return <div className="text-xs">{label}</div>;
    },
  },
  {
    header: 'Invite Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      const status = row.original.status;
      const invitationStatus = row.original.invitationStatus;

      // User has accepted invitation or never had one - show user status instead
      const shouldShowUserStatus =
        !invitationStatus || invitationStatus === UserInviteStatus.ACCEPTED;

      const displayStatus = shouldShowUserStatus
        ? USER_STATUS_LABELS[status]
        : INVITE_STATUS_LABELS[invitationStatus];

      return (
        <div
          className={`inline-flex rounded-full px-2 py-1 text-xs ${getStatusChipClass(shouldShowUserStatus ? status : invitationStatus)}`}
        >
          {displayStatus}
        </div>
      );
    },
  },
  {
    header: 'Actions',
    accessorKey: 'actions',
    cell: ({ row }) => {
      return <ActionDropdown user={row.original} />;
    },
  },
];
